import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import history from 'browserHistory'
import { isDetachedMode } from 'helpers/URLHelper'

import ErrorBoundary from 'components/Utils/ErrorBoundary'

import AppLoading from './Layout/AppLoading'

import { CollabConnectionProvider } from '@slite/editor'
import SearchTokenRefresh from './SearchTokenRefresh'
import GraphQLProvider from './GraphQLProvider'
import TokenRenewer from './TokenRenewer'
import ColorModeSync from './ColorModeSync'

import { selectors as OrganizationsSelectors } from 'data/organizations'
import { selectors as UsersSelectors } from 'data/users'
import { actions as UIActions } from 'data/ui'
import { selectors as AuthSelectors } from 'data/auth'
import DesktopHelpers from 'helpers/DesktopHelpers'
import { selectors as UserPreferencesSelectors } from 'data/userPreferences'
import * as AppSelectors from 'data/app/selectors'

import { initAnalytics } from 'data/configureStore/middleware/analytics'

import PaginatedUsersDataSync from 'components/Users/dataSync'
import OrganizationDataSync from 'components/Organizations/dataSync'
import {
  DESKTOP_REDIRECTION,
  CHANNEL_CLONE,
} from 'components/Utils/Modal/constants'
import ModalContainer from 'components/Utils/Modal/Container'
import ApplicationsDataSync from 'components/Applications/dataSync'

import RoutesHelper from 'helpers/RoutesHelper'
import AppSubscribers from 'components/Layout/AppSubscribers'
import { setActiveModal } from 'data/ui/actions'
import config from 'config'

function Logged({ children }) {
  const dispatch = useDispatch()

  const analyticsInitialized = React.useRef(false)
  const hasOpenedDesktopApp = React.useRef(false)

  const currentUser = useSelector(UsersSelectors.getCurrentUser)
  const organization = useSelector(OrganizationsSelectors.getOrganization)

  const isSignedIn = useSelector(AppSelectors.isSignedIn)
  const shouldSignIn = useSelector(AppSelectors.shouldSignIn)
  const isLoaded = Boolean(currentUser) && Boolean(organization)

  const apiToken = useSelector(AuthSelectors.getApiToken)
  const hasPreviousLocation = useSelector(state =>
    Boolean(state.ui.previousLocation)
  )
  const isDesktopUser = useSelector(UserPreferencesSelectors.isDesktopUser)

  const isAppDesktop = DesktopHelpers.isAppDesktop()

  const redirectToDesktopApp =
    !hasPreviousLocation && isDesktopUser && !isAppDesktop && !isDetachedMode()

  React.useEffect(
    function onMount() {
      if (isDetachedMode()) {
        dispatch(UIActions.setDetachedMode())
      }
    },
    [dispatch]
  )

  React.useEffect(
    function handleElectronUrlLoad() {
      if (window.electron) {
        window.electron.ipcRenderer.on('load-url', (event, url) => {
          history.replace(url)

          // The modal_duplicate opening is done on app bootstrap only
          // In order to be able to handle channel duplication on desktop app
          // we need to handle this specific case here and open the modal if necessary.
          // This is not and ideal solution for now but will be made generic if needed.
          const result = /modal_duplicate_channel=(\w+)/.exec(url)
          const publicShareToken = result && result[1]

          if (publicShareToken) {
            dispatch(
              setActiveModal({
                id: CHANNEL_CLONE,
                meta: { publicShareToken },
              })
            )
          }
        })
      }
    },
    [dispatch]
  )

  React.useEffect(
    function checkIfShouldRedirect() {
      if (shouldSignIn) {
        history.replace(RoutesHelper.login())
      }
    },
    [shouldSignIn]
  )

  React.useEffect(
    function checkIfShouldOpenDesktopApp() {
      if (redirectToDesktopApp && !hasOpenedDesktopApp.current) {
        hasOpenedDesktopApp.current = true
        const desktopUrl = `${window.location.href.replace(
          window.location.protocol,
          'slite:'
        )}`
        if (!DesktopHelpers.isChrome()) {
          window.open(desktopUrl, '_blank')
        } else {
          window.location = desktopUrl
        }
        dispatch(setActiveModal({ id: DESKTOP_REDIRECTION }))
      }
    },
    [dispatch, redirectToDesktopApp]
  )

  React.useEffect(
    function checkIfShouldInitAnalytics() {
      if (analyticsInitialized.current) {
        return
      }
      if (config.appVersion && currentUser && organization) {
        dispatch({
          type: 'INIT_ANALYTICS',
          meta: {
            analytics: initAnalytics(
              config.appVersion,
              currentUser,
              organization
            ),
          },
        })
        analyticsInitialized.current = true
      }
    },
    [dispatch, currentUser, organization]
  )

  const setCollabConnectionStatus = React.useCallback(
    status => {
      dispatch(UIActions.setConnectionStatus({ service: 'collab', status }))
    },
    [dispatch]
  )

  if (!isSignedIn) {
    return <AppLoading />
  }

  return (
    <GraphQLProvider>
      {/* This will load all the users in a sequence of paginated calls  */}
      <PaginatedUsersDataSync />
      <OrganizationDataSync>
        <ApplicationsDataSync>
          <ColorModeSync />
          <CollabConnectionProvider
            apiToken={apiToken}
            collabEndpoint={config.collabEndpoint}
            onConnectionChange={setCollabConnectionStatus}
          >
            <SearchTokenRefresh />
            <TokenRenewer>
              <ErrorBoundary>
                <AppSubscribers />
                <ModalContainer />
                {isLoaded && children}
              </ErrorBoundary>
            </TokenRenewer>
          </CollabConnectionProvider>
        </ApplicationsDataSync>
      </OrganizationDataSync>
    </GraphQLProvider>
  )
}

export default React.memo(Logged)
